import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import natsort from 'natsort';
import { cloneElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { LAYOUT_TOOLS } from "../tools/useLayoutAnnotations";

export default function DialogCreation({ open, onClose, dialogData, addNewLayer, layers, children }) {
    const { t } = useTranslation();
    const childrenRef = useRef(null);
    const [isSaveDisabled, setIsSaveDisabled] = useState(false);

    // The dialog stays mounted between forms, so a form's disabled state must not carry over to the next one.
    useEffect(() => {
        if (!open) setIsSaveDisabled(false);
    }, [open]);

    const currentTool = useMemo(() => dialogData?.tool, [dialogData]);

    const { globalLayers } = useSelector(state => state.layouts);
    const sortedGlobalLayers = useMemo(() => globalLayers?.layers?.map(({ name }) => name)?.sort(natsort({ insensitive: true })) || [], [globalLayers]);

    const TEXTS = useMemo(() => {
        return {
            [LAYOUT_TOOLS.ZONE]: { title: t('overseer_app.layouts.creation.new_zone', 'New Zone') },
            [LAYOUT_TOOLS.AISLE]: { title: t('overseer_app.layouts.creation.new_aisle', 'New Aisle') },
            [LAYOUT_TOOLS.EXCLUSION_ZONE]: { title: t('overseer_app.layouts.creation.new_exclusion_zone', 'New Exclusion Zone') },
            [LAYOUT_TOOLS.AISLE_SIDE]: { title: t('overseer_app.layouts.creation.add_linear', 'Add linear') },
        }
    }, [t]);

    const onSave = useCallback(() => {
        if (childrenRef.current) {
            const result = childrenRef.current.callbackOnSave();
            if (result) {
                onClose();
            }
        }
    }, [onClose, childrenRef]);

    
    const isUniqueName = useCallback((name, layer, subLayer = null) => {
      const layerData = layers?.[layer]?.layers || {};
      if (subLayer) {
        const subLayerData = Object.keys(layers?.[layer]?.layers?.[subLayer]?.subLayers || {}) || [];

        return !subLayerData?.some(subLayer => subLayer?.toLowerCase() === name?.toLowerCase());
      }
      return !Object.keys(layerData)?.some(layer => layer?.toLowerCase() === name?.toLowerCase());
    }, [layers]);

    return <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ marginBottom: -1 }}>
            {TEXTS?.[currentTool]?.title || t('overseer_app.layouts.creation.new_annotation', 'New Annotation')}
        </DialogTitle>
        <DialogContent>
            {children && cloneElement(children, {
                ref: childrenRef,
                addNewLayer,
                dialogData,
                globalLayers: sortedGlobalLayers,
                isUniqueName,
                onDisabledChange: setIsSaveDisabled,
            })}
        </DialogContent>
        <DialogActions>
            <Button color="secondary" onClick={onClose}>{t('overseer_app.layouts.cancel', 'Cancel')}</Button>
            <Button variant="contained" color="primary" onClick={onSave} disabled={isSaveDisabled}>{t('overseer_app.layouts.save', 'Save')}</Button>
        </DialogActions>
    </Dialog>
}